'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { EASE_OUT } from '@/lib/animations';

interface ComingSoonModalProps {
  open: boolean;
  onClose: () => void;
  productName?: string;
}

export default function ComingSoonModal({ open, onClose, productName }: ComingSoonModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="glass-card rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-2xl relative text-center">
              {/* Close */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>

              {/* Icon */}
              <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-7 h-7 text-blue-600" />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-3">
                Demo en camino
              </h3>
              <p className="text-slate-500 leading-relaxed mb-2">
                Estamos trabajando en la demo pública
                {productName ? (
                  <>
                    {' '}de <span className="font-semibold text-slate-700">{productName}</span>
                  </>
                ) : null}.
              </p>
              <p className="text-sm text-slate-400 mb-8">
                Mientras tanto, te enseñamos cómo funcionaría en tu negocio en una llamada rápida de 30 minutos.
              </p>

              {/* CTAs */}
              <div className="flex flex-col gap-3">
                <a
                  href="https://calendly.com/innovatheratech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_4px_16px_rgba(37,99,235,0.3)] hover:bg-blue-700 transition-all active:scale-95"
                >
                  <Calendar className="w-4 h-4" />
                  Reservar demo privada
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <button
                  onClick={onClose}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200/70 bg-white/60 px-6 py-3 text-sm font-semibold text-slate-600 hover:bg-white/80 hover:border-slate-300 transition-all"
                >
                  Cerrar
                </button>
              </div>

              {/* Badge */}
              <p className="mt-5 text-[11px] text-slate-400 font-mono">
                Lanzamiento próximamente
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
